import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AccountsApiService, ActivationStateRequest, ActivationStateResponse } from '@vendasta/wsp-accounts';
import { SelectedSiteService } from './selected-site.service';
import { SelectedAccountService } from './selected-account.service';
import { MULTISITE_EDITION_NAME } from './../globals';

const ProEditionID = 'pro-edition';
const TemplateEditionID = 'template-edition';
const ProEditionName = 'Pro';
const ExpressEditionName = 'Standard';
const TemplateEditionName = 'Template';
const MultisiteEditionId = 'multisite';
const MultisiteEditionName = MULTISITE_EDITION_NAME;

@Injectable()
export class AccountsService implements OnDestroy {
  completeSubscriptions$: Subject<string> = new Subject<string>();
  private editionId$$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  readonly editionId$: Observable<string> = this.editionId$$.asObservable();
  private isExpress$$: BehaviorSubject<boolean> = new BehaviorSubject(undefined);
  readonly isExpress$: Observable<boolean> = this.isExpress$$.asObservable();
  private marketId$$: BehaviorSubject<string> = new BehaviorSubject(undefined);
  readonly marketId$: Observable<string> = this.marketId$$.asObservable();
  private editionName$$: BehaviorSubject<string> = new BehaviorSubject('');
  readonly editionName$: Observable<string> = this.editionName$$.asObservable();

  constructor(
    private accountsApiService: AccountsApiService,
    private selectedSiteService: SelectedSiteService,
    private selectedAccountService: SelectedAccountService,
  ) {
    const reqWithSiteId: Observable<ActivationStateRequest> = this.selectedSiteService.getSiteId().pipe(
      filter((siteId: string) => !!siteId),
      map((siteId: string) => {
        return { siteId: siteId, accountId: '' } as ActivationStateRequest;
      }),
    );

    const reqWithAccountId: Observable<ActivationStateRequest> = this.selectedAccountService.getAccountId().pipe(
      filter((accountId: string) => !!accountId),
      map((accountId: string) => {
        return { siteId: '', accountId: accountId } as ActivationStateRequest;
      }),
    );

    merge(reqWithSiteId, reqWithAccountId)
      .pipe(
        switchMap((req: ActivationStateRequest) => this.accountsApiService.getActivationState(req)),
        filter((resp: ActivationStateResponse) => !!resp && !!resp.activationState),
        map((resp: ActivationStateResponse) => [resp.activationState.editionId, resp.activationState.marketId]),
        tap(([editionId, marketId]) => {
          this.editionId$$.next(editionId);
          this.isExpress$$.next(
            editionId !== ProEditionID && editionId !== TemplateEditionID && editionId !== MultisiteEditionId,
          );
          this.marketId$$.next(marketId);
          let editionName = '';
          switch (editionId) {
            case ProEditionID:
              editionName = ProEditionName;
              break;
            case MultisiteEditionId:
              editionName = MultisiteEditionName;
              break;
            case TemplateEditionID:
              editionName = TemplateEditionName;
              break;
            default:
              editionName = ExpressEditionName;
              break;
          }
          this.editionName$$.next(editionName);
        }),
        shareReplay(1),
        takeUntil(this.completeSubscriptions$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.completeSubscriptions$.next('destroy');
    this.completeSubscriptions$.complete();
  }
}
